// @ts-ignore
import SlimSelect from 'slim-select';

// @ts-ignore
import './slimselect.utils.css';

// ----------------------------------------------------------------------------
// Type Definitions
// ----------------------------------------------------------------------------

declare global {
  interface HTMLSelectElement {
    slim?: SlimSelect;
  }
}

/** The structure of the `data-slimselect` JSON attribute */
interface SlimSelectDataAttrs {
  tags?: boolean;
  selectAll?: string;
  ajax?: {
    url: string;
    delay?: number;
  };
  /** Language override (optional) – if not set, uses `document.documentElement.lang` */
  language?: 'en' | 'it' | string;
  [key: string]: any; // allow any other SlimSelect options
}

/** Full settings after merging defaults, translations and user overrides */
interface SlimSelectFullSettings extends SlimSelectDataAttrs {
  placeholderText: string;
  searchText: string;
  searchingText: string;
  searchPlaceholder: string;
  hideSelected: boolean;
  maxValuesShown: number;
}

// ----------------------------------------------------------------------------
// Constants & Translations
// ----------------------------------------------------------------------------

const whitespaceRegex = /^\s+/g;

/** Base (non‑translated) defaults */
const baseDefaults: Pick<SlimSelectFullSettings, 'hideSelected' | 'maxValuesShown'> = {
  hideSelected: false,
  maxValuesShown: 50,
};

/** Translation maps for supported languages */
const translations: Record<string, Partial<Omit<SlimSelectFullSettings, 'hideSelected' | 'maxValuesShown'>>> = {
  en: {
    placeholderText: 'Select',
    searchText: 'No results found',
    searchingText: 'Searching...',
    searchPlaceholder: 'Search...',
  },
  it: {
    placeholderText: 'Seleziona',
    searchText: 'Nessun risultato trovato',
    searchingText: 'Ricerca in corso...',
    searchPlaceholder: 'Cerca...',
  },
};

/** Get the effective language from the document or from a user override */
function getLanguage(userLanguage?: string): string {
  if (userLanguage && translations[userLanguage]) {
    return userLanguage;
  }
  const docLang = document.documentElement.lang || 'en';
  return translations[docLang] ? docLang : 'en';
}

// ----------------------------------------------------------------------------
// Internal Initialization
// ----------------------------------------------------------------------------

function _initSlimSelect(element: HTMLSelectElement): SlimSelect {
  // Destroy existing instance if any
  if (element.slim) {
    element.slim.destroy();
  }

  // Parse user settings from data attribute (assume valid JSON)
  let userSettings: SlimSelectDataAttrs = {};
  try {
    userSettings = JSON.parse(element.dataset.slimselect || '{}');
  } catch {
    // fallback to empty object
  }

  // Determine language (user override or document default)
  const lang = getLanguage(userSettings.language);

  // Build the final settings: base + translations + user overrides
  //@ts-ignore
  const fullSettings: SlimSelectFullSettings = {
    ...baseDefaults,
    ...translations[lang],
    ...userSettings,
  };

  // Build events object
  const events: Record<string, any> = {};

  // Tags: custom addable validator
  if (fullSettings.tags) {
    events.addable = (value: string) => {
      if (value.match(whitespaceRegex)) {
        return false;
      }
      return { text: value, value };
    };
  }

  // AJAX: remote search with debounce
  if (fullSettings.ajax?.url) {
    let timeoutId: number | undefined;
    events.search = (search: string, /*currentData: any[]*/) => {
      return new Promise((resolve, reject) => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          if (search.length < 2) {
            reject('Inserisci almeno 2 caratteri');
            return;
          }
          const url = `${fullSettings.ajax!.url}&query=${search}`;
          fetch(url)
            .then((res) => res.json())
            .then((data) => resolve(data))
            .catch(reject);
        }, fullSettings.ajax!.delay || 300);
      });
    };
  }

  // Instantiate SlimSelect
  const ss = new SlimSelect({
    select: element,
    settings: fullSettings,
    events,
  });

  // "Select All" feature
  if (fullSettings.selectAll) {
    ss.events.beforeOpen = function () {
      const data = ss.getData();
      data.forEach((item: any) => {
        if (item.selectAll === false) {
          item.selectAll = true;
          item.selectAllText = fullSettings.selectAll;
        }
      });
      ss.setData(data);
    };
  }

  // Store reference on the element
  element.slim = ss;
  return ss;
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

/**
 * Initialises all `<select>` elements with `data-slimselect`.
 * Call this after DOM ready.
 */
export function initSlimSelects(): void {
  const selects = document.querySelectorAll<HTMLSelectElement>('[data-slimselect]');
  selects.forEach((el) => _initSlimSelect(el));
}

/**
 * Initialises a specific `<select>` by CSS selector and optionally selects a value.
 * @param elementSelector - CSS selector for the target `<select>`
 * @param selectedValue - optional value to preselect
 * @returns The SlimSelect instance
 */
export function initSlimSelect(
  elementSelector: string,
  selectedValue = ''
): SlimSelect {
  const element = document.querySelector<HTMLSelectElement>(elementSelector);
  if (!element) {
    throw new Error(`Element not found for selector: ${elementSelector}`);
  }
  const ss = _initSlimSelect(element);
  if (selectedValue) {
    ss.setSelected(selectedValue);
  }
  return ss;
}

document.addEventListener('DOMContentLoaded', initSlimSelects);
